import React from 'react';
import { Label, Tooltip } from '@patternfly/react-core';
import { ArchiveIcon, ClockIcon } from '@patternfly/react-icons';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import { isSubjectStale } from './backupTree';
import type { BackupRun, BackupSummary } from '../../../types/models/Backup';

/**
 * Badge helpers for the backup inventory, in the idiom of `vmStatusLabel.tsx`.
 *
 * The three signals are **independent**, never merged into one status column: a subject can be
 * retired *and* missing at the same time (someone deleted a `.old` tree), and either can coexist
 * with staleness. Callers render a list of badges, not a single value.
 *
 * The tooltips are supplementary — each badge's visible text already states the fact, so a reader
 * that never surfaces the tooltip still gets the meaning.
 */

/**
 * The VM was decommissioned and its directory renamed `<vm>.old`; its backups are deliberately
 * kept and still consume space. **Benign and expected** — deliberately neutral, never red, and
 * never presented as an incident.
 */
export const renderRetiredLabel = (): React.ReactNode => (
  <Tooltip content="The VM was decommissioned; its backups are deliberately kept.">
    <Label isCompact variant="outline" color="grey" icon={<ArchiveIcon />}>
      Retired
    </Label>
  </Tooltip>
);

/**
 * The directory **vanished** from the backup server on `missingSince` — an alertable fact, not a
 * cosmetic one. The row and its history are kept, and the flag clears itself the moment the
 * directory reappears in a report, so it must never be persisted client-side.
 */
export const renderMissingLabel = (missingSince: string): React.ReactNode => (
  <Tooltip content="This directory is no longer on the backup server. No new restore points are being created for it.">
    <Label isCompact status="warning">
      Missing since {formatWallClockDateTime(missingSince)}
    </Label>
  </Tooltip>
);

/**
 * The subject's last backup is older than the threshold the backend published — or it has never
 * run at all. Rendered **only** where a threshold was supplied; a surface without the summary
 * call passes `null` and shows the factual age with no verdict attached.
 */
export const renderStaleLabel = (staleAfterHours: number): React.ReactNode => (
  <Tooltip content={`No backup in the last ${staleAfterHours} hours.`}>
    <Label isCompact color="orange" icon={<ClockIcon />}>
      Stale
    </Label>
  </Tooltip>
);

/**
 * Every state badge a subject currently warrants, in a stable order. Returns an empty array for a
 * healthy subject, which is the common case and correctly renders nothing.
 *
 * `staleAfterHours` is `null` wherever the inventory summary was not fetched (the VM detail tab),
 * which suppresses the stale verdict rather than hardcoding the threshold.
 */
export const renderSubjectStateLabels = (
  subject: BackupSummary,
  staleAfterHours: number | null,
): React.ReactNode[] => {
  const labels: React.ReactNode[] = [];
  if (subject.retired) {
    labels.push(<React.Fragment key="retired">{renderRetiredLabel()}</React.Fragment>);
  }
  if (subject.missing_since !== null) {
    labels.push(<React.Fragment key="missing">{renderMissingLabel(subject.missing_since)}</React.Fragment>);
  }
  if (staleAfterHours !== null && isSubjectStale(subject, staleAfterHours)) {
    labels.push(<React.Fragment key="stale">{renderStaleLabel(staleAfterHours)}</React.Fragment>);
  }
  return labels;
};

/**
 * The badge slot for an individual **restore point**.
 *
 * Deliberately empty today. A "tested / verified" label — meaning *this restore point was
 * test-restored* — is planned, and it attaches to a single run, not to the VM. Building the run
 * row's first cell as a badge list now means that label drops in here without restructuring the
 * table or adding a column.
 *
 * The backend has **no such field**: nothing is invented, typed or mocked for it, and the `run`
 * parameter is accepted only so the future implementation has it to hand.
 */
export const renderRunVerificationLabels = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  run: BackupRun,
): React.ReactNode[] => [];
